/**
 * ScheduleFormDialog
 *
 * Create / edit a recurring schedule for an agent: cadence (daily/weekly/monthly),
 * local time + timezone, recipient emails, and (for manual-report agents only)
 * the reporting window the pipeline should fill when the schedule fires.
 *
 * Monday vs Thursday windows are two separate schedules — each schedule owns
 * its own day + period_window (e.g. Mon → previous Mon–Sun, Thu → this Mon–Wed).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type {
  CreatePlaybookScheduleRequest,
  PlaybookSchedule,
  ScheduleCadence,
  SchedulePeriodWindow,
} from '../../../types/playbooks';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Suggested reporting window for common the analyst-style fire days. */
function suggestedWindowForDay(dayOfWeek: number): SchedulePeriodWindow {
  // Thursday → this week Mon–Wed (partial week so far)
  if (dayOfWeek === 3) {
    return { type: 'week_relative', week: 'current', start_weekday: 0, end_weekday: 2 };
  }
  // Monday (default) → previous full week Mon–Sun
  return { type: 'week_relative', week: 'previous', start_weekday: 0, end_weekday: 6 };
}

function describeSuggestion(dayOfWeek: number): string {
  if (dayOfWeek === 0) return 'Suggested for Monday: previous week Mon–Sun';
  if (dayOfWeek === 3) return 'Suggested for Thursday: this week Mon–Wed';
  return 'Adjust the window to match what this run should fill';
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function allTimezones(): string[] {
  try {
    const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    if (typeof fn === 'function') return fn('timeZone');
  } catch {
    /* fall through */
  }
  return ['UTC', browserTimezone()];
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export interface ScheduleFormDialogProps {
  open: boolean;
  /** When provided, the dialog edits this schedule; otherwise it creates a new one. */
  schedule?: PlaybookSchedule | null;
  /** When true, show Custom parameters and require period_window (manual pipelines). */
  requirePeriodWindow?: boolean;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (payload: CreatePlaybookScheduleRequest) => void;
}

export default function ScheduleFormDialog({
  open,
  schedule,
  requirePeriodWindow = false,
  saving,
  onClose,
  onSubmit,
}: ScheduleFormDialogProps) {
  const isEdit = Boolean(schedule);
  const timezones = useMemo(allTimezones, []);

  const [label, setLabel] = useState('');
  const [cadence, setCadence] = useState<ScheduleCadence>('weekly');
  const [time, setTime] = useState('09:00');
  const [timezone, setTimezone] = useState(browserTimezone());
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [emails, setEmails] = useState<string[]>([]);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const [periodWeek, setPeriodWeek] = useState<'previous' | 'current'>('previous');
  const [periodStart, setPeriodStart] = useState(0);
  const [periodEnd, setPeriodEnd] = useState(6);

  const applySuggestedWindow = (dow: number) => {
    const suggested = suggestedWindowForDay(dow);
    setPeriodWeek(suggested.week);
    setPeriodStart(suggested.start_weekday);
    setPeriodEnd(suggested.end_weekday);
  };

  useEffect(() => {
    if (!open) return;
    if (schedule) {
      setLabel(schedule.label ?? '');
      setCadence(schedule.cadence);
      setTime(schedule.time_of_day);
      setTimezone(schedule.timezone);
      setDayOfWeek(schedule.day_of_week ?? 0);
      setDayOfMonth(schedule.day_of_month ?? 1);
      setEmails(schedule.recipient_emails ?? []);
      if (schedule.period_window) {
        setPeriodWeek(schedule.period_window.week);
        setPeriodStart(schedule.period_window.start_weekday);
        setPeriodEnd(schedule.period_window.end_weekday);
      } else {
        applySuggestedWindow(schedule.day_of_week ?? 0);
      }
    } else {
      setLabel('');
      setCadence('weekly');
      setTime('09:00');
      setTimezone(browserTimezone());
      setDayOfWeek(0);
      setDayOfMonth(1);
      setEmails([]);
      applySuggestedWindow(0);
    }
    setEmailDraft('');
    setEmailError(null);
  }, [open, schedule]);

  const handleDayOfWeekChange = (dow: number) => {
    setDayOfWeek(dow);
    if (requirePeriodWindow) {
      applySuggestedWindow(dow);
    }
  };

  const commitEmail = (raw: string): boolean => {
    const value = raw.trim().toLowerCase();
    if (!value) return true;
    if (!EMAIL_RE.test(value)) {
      setEmailError(`"${raw.trim()}" is not a valid email`);
      return false;
    }
    if (!emails.includes(value)) setEmails((prev) => [...prev, value]);
    setEmailDraft('');
    setEmailError(null);
    return true;
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      commitEmail(emailDraft);
    } else if (e.key === 'Backspace' && !emailDraft && emails.length) {
      setEmails((prev) => prev.slice(0, -1));
    }
  };

  const buildPeriodWindow = (): SchedulePeriodWindow | null => {
    if (!requirePeriodWindow) return null;
    if (periodStart > periodEnd) return null;
    return {
      type: 'week_relative',
      week: periodWeek,
      start_weekday: periodStart,
      end_weekday: periodEnd,
    };
  };

  const periodWindow = buildPeriodWindow();
  const periodValid =
    !requirePeriodWindow ||
    (periodWindow != null && periodWindow.start_weekday <= periodWindow.end_weekday);

  const canSubmit =
    emails.length > 0 &&
    !!time &&
    !!timezone &&
    !saving &&
    !emailDraft.trim() &&
    periodValid;

  const handleSubmit = () => {
    if (emailDraft.trim() && !commitEmail(emailDraft)) return;
    const finalEmails = emailDraft.trim() && EMAIL_RE.test(emailDraft.trim().toLowerCase())
      ? Array.from(new Set([...emails, emailDraft.trim().toLowerCase()]))
      : emails;
    if (finalEmails.length === 0) {
      setEmailError('Add at least one recipient email');
      return;
    }
    const window = buildPeriodWindow();
    if (requirePeriodWindow && !window) return;
    onSubmit({
      label: label.trim() || null,
      // New schedules are always enabled; edits preserve the list-row toggle.
      enabled: schedule?.enabled ?? true,
      cadence,
      time_of_day: time,
      timezone,
      day_of_week: cadence === 'weekly' ? dayOfWeek : null,
      day_of_month: cadence === 'monthly' ? dayOfMonth : null,
      recipient_emails: finalEmails,
      period_window: window,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit schedule' : 'New schedule'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField
            label="Name (optional)"
            placeholder="e.g. Monday prior-week fill"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            size="small"
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="cadence-label">Frequency</InputLabel>
              <Select
                labelId="cadence-label"
                label="Frequency"
                value={cadence}
                onChange={(e) => setCadence(e.target.value as ScheduleCadence)}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>

            {cadence === 'weekly' && (
              <FormControl size="small" fullWidth>
                <InputLabel id="dow-label">Day</InputLabel>
                <Select
                  labelId="dow-label"
                  label="Day"
                  value={dayOfWeek}
                  onChange={(e) => handleDayOfWeekChange(Number(e.target.value))}
                >
                  {WEEKDAYS.map((d, i) => (
                    <MenuItem key={d} value={i}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {cadence === 'monthly' && (
              <FormControl size="small" fullWidth>
                <InputLabel id="dom-label">Day of month</InputLabel>
                <Select
                  labelId="dom-label"
                  label="Day of month"
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(Number(e.target.value))}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              size="small"
              sx={{ width: { xs: '100%', sm: 140 } }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
            <Autocomplete
              options={timezones}
              value={timezone}
              onChange={(_, v) => v && setTimezone(v)}
              disableClearable
              fullWidth
              renderInput={(params) => (
                <TextField {...params} label="Timezone" size="small" />
              )}
            />
          </Stack>
          {cadence === 'monthly' && dayOfMonth > 28 && (
            <Typography variant="caption" color="text.secondary">
              Months with fewer days will use the last day of that month.
            </Typography>
          )}

          <Box>
            <TextField
              label="Recipient emails"
              placeholder="Type an email and press Enter"
              value={emailDraft}
              onChange={(e) => {
                setEmailDraft(e.target.value);
                if (emailError) setEmailError(null);
              }}
              onKeyDown={handleEmailKeyDown}
              onBlur={() => emailDraft.trim() && commitEmail(emailDraft)}
              error={Boolean(emailError)}
              helperText={emailError || 'Press Enter, comma, or space to add. External addresses are allowed.'}
              fullWidth
              size="small"
            />
            {emails.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {emails.map((e) => (
                  <Chip
                    key={e}
                    label={e}
                    size="small"
                    onDelete={() => setEmails((prev) => prev.filter((x) => x !== e))}
                  />
                ))}
              </Stack>
            )}
          </Box>

          {requirePeriodWindow && (
            <>
              <Divider />
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">Custom parameters</Typography>
                <Typography variant="caption" color="text.secondary">
                  Reporting window for this scheduled run. Use two schedules:
                  Monday → previous Mon–Sun, Thursday → this Mon–Wed.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="week-label">Week</InputLabel>
                    <Select
                      labelId="week-label"
                      label="Week"
                      value={periodWeek}
                      onChange={(e) => setPeriodWeek(e.target.value as 'previous' | 'current')}
                    >
                      <MenuItem value="previous">Previous week</MenuItem>
                      <MenuItem value="current">Current week</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="start-label">From</InputLabel>
                    <Select
                      labelId="start-label"
                      label="From"
                      value={periodStart}
                      onChange={(e) => setPeriodStart(Number(e.target.value))}
                    >
                      {WEEKDAYS.map((d, i) => (
                        <MenuItem key={d} value={i}>{d}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="end-label">Through</InputLabel>
                    <Select
                      labelId="end-label"
                      label="Through"
                      value={periodEnd}
                      onChange={(e) => setPeriodEnd(Number(e.target.value))}
                    >
                      {WEEKDAYS.map((d, i) => (
                        <MenuItem key={d} value={i} disabled={i < periodStart}>{d}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                {cadence === 'weekly' && (
                  <Typography variant="caption" color="text.secondary">
                    {describeSuggestion(dayOfWeek)}. Changing Day auto-fills the common pairing.
                  </Typography>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
          {isEdit ? 'Save changes' : 'Create schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
